import {
  hoursToMilliseconds,
  minutesToMilliseconds,
  secondsToMilliseconds,
  subMilliseconds,
} from 'date-fns'

export type Timeframe = {
  label?: string
  key?: string
  startDate?: Date
  endDate?: Date
  intervalInSeconds: number
}

type TimeframePreset = {
  key: string
  label: string
  durationMs: number
  intervalInSeconds: number
}

const MINUTE = 60

const TIMEFRAME_PRESETS: TimeframePreset[] = [
  {
    key: '1h',
    label: 'Last 1 hour',
    durationMs: hoursToMilliseconds(1),
    intervalInSeconds: MINUTE,
  },
  {
    key: '6h',
    label: 'Last 6 hours',
    durationMs: hoursToMilliseconds(6),
    intervalInSeconds: MINUTE,
  },
  {
    key: '12h',
    label: 'Last 12 hours',
    durationMs: hoursToMilliseconds(12),
    intervalInSeconds: MINUTE,
  },
  {
    key: '1d',
    label: 'Last 1 day',
    durationMs: hoursToMilliseconds(24),
    intervalInSeconds: MINUTE,
  },
  {
    key: '2d',
    label: 'Last 2 days',
    durationMs: hoursToMilliseconds(2 * 24),
    intervalInSeconds: 5 * MINUTE,
  },
  {
    key: '4d',
    label: 'Last 4 days',
    durationMs: hoursToMilliseconds(4 * 24),
    intervalInSeconds: 15 * MINUTE,
  },
  {
    key: '7d',
    label: 'Last 7 days',
    durationMs: hoursToMilliseconds(7 * 24),
    intervalInSeconds: 30 * MINUTE,
  },
  {
    key: '14d',
    label: 'Last 14 days',
    durationMs: hoursToMilliseconds(14 * 24),
    intervalInSeconds: 60 * MINUTE,
  },
  {
    key: '30d',
    label: 'Last 30 days',
    durationMs: hoursToMilliseconds(30 * 24),
    intervalInSeconds: 2 * 60 * MINUTE,
  },
]

const alignToInterval = (date: Date, intervalInSeconds: number) => {
  const intervalMs = secondsToMilliseconds(intervalInSeconds)
  return new Date(Math.floor(date.getTime() / intervalMs) * intervalMs)
}

const toTimeframe = (preset: TimeframePreset, now: Date): Timeframe => ({
  key: preset.key,
  label: preset.label,
  startDate: alignToInterval(
    subMilliseconds(now, preset.durationMs),
    preset.intervalInSeconds
  ),
  endDate: now,
  intervalInSeconds: preset.intervalInSeconds,
})

export const getTimeframes = (now = new Date()): Timeframe[] =>
  TIMEFRAME_PRESETS.map((preset) => toTimeframe(preset, now))

export const getPresetTimeframe = (
  key?: string | null,
  now = new Date()
): Timeframe | undefined => {
  const preset = TIMEFRAME_PRESETS.find((item) => item.key === key)
  return preset ? toTimeframe(preset, now) : undefined
}

export const isLiveTimeframe = (timeframe: Timeframe) => Boolean(timeframe.key)

const MIN_REFRESH_INTERVAL_MS = secondsToMilliseconds(30)
const MAX_REFRESH_INTERVAL_MS = minutesToMilliseconds(5)

export const getRefreshIntervalMs = ({ intervalInSeconds }: Timeframe) => {
  const halfStepMs = secondsToMilliseconds(intervalInSeconds) / 2
  return Math.min(
    Math.max(halfStepMs, MIN_REFRESH_INTERVAL_MS),
    MAX_REFRESH_INTERVAL_MS
  )
}

export const getIntervalInSeconds = (
  startDateDate: Date,
  endDateDate: Date
) => {
  const diffInMs = endDateDate.getTime() - startDateDate.getTime()
  const diffInMinutes = Math.ceil(diffInMs / (1000 * 60))

  if (diffInMinutes <= 60) {
    return 60 // 1 minute
  } else if (diffInMinutes <= 6 * 60) {
    return 5 * 60 // 5 minutes
  } else if (diffInMinutes <= 12 * 60) {
    return 5 * 60 // 5 minutes
  } else if (diffInMinutes <= 24 * 60) {
    return 10 * 60 // 10 minutes
  } else if (diffInMinutes <= 2 * 24 * 60) {
    return 30 * 60 // 30 minutes
  } else if (diffInMinutes <= 4 * 24 * 60) {
    return 60 * 60 // 1 hour
  } else if (diffInMinutes <= 7 * 24 * 60) {
    return 120 * 60 // 2 hours
  } else if (diffInMinutes <= 14 * 24 * 60) {
    return 120 * 60 // 2 hours
  } else {
    return 240 * 60 // 4 hours
  }
}

/**
 * Returns the appropriate date format for X axis labels based on timeframe duration
 * @param timeframe - The timeframe object containing start and end dates
 * @returns Date format string for date-fns format function
 */
export const getXAxisDateFormat = (timeframe: Timeframe): string => {
  const diffInMs = timeframe.endDate!.getTime() - timeframe.startDate!.getTime()
  const diffInHours = diffInMs / (1000 * 60 * 60)

  if (diffInHours <= 1) {
    // For 1 hour or less: show hours and minutes (e.g., "14:30")
    return 'HH:mm'
  } else if (diffInHours <= 24) {
    // For up to 1 day: show hours and minutes (e.g., "14:30")
    return 'HH:mm'
  } else if (diffInHours <= 7 * 24) {
    // For up to 7 days: show month, day, and hours (e.g., "Aug 8 14:00")
    return 'MMM dd HH:mm'
  } else {
    // For longer periods: show short month and day (e.g., "Aug 8")
    return 'MMM dd'
  }
}
